import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

function Dashboard({ marketData }) {
  const { user } = useAuth();
  const [wallet, setWallet] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const tradingPairs = [
    'BTC/USDT', 'ETH/USDT', 'XRP/USDT', 'ADA/USDT', 
    'DOT/USDT', 'LINK/USDT', 'XLM/USDT', 'XMR/USDT',
    'DOGE/USDT', 'TRX/USDT'
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token found');
        setWallet([]);
        setLoading(false);
        return;
      }

      const walletResponse = await axios.get('https://trcsite.net/api/wallet', {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Dashboard Wallet Response:', walletResponse.data);
      
      let walletData = [];
      
      if (walletResponse.data && Array.isArray(walletResponse.data)) {
        walletData = walletResponse.data;
      } else if (walletResponse.data && walletResponse.data.wallets && Array.isArray(walletResponse.data.wallets)) {
        walletData = walletResponse.data.wallets;
      } else if (walletResponse.data && Array.isArray(walletResponse.data)) {
        walletData = walletResponse.data;
      } else {
        walletData = walletResponse.data.wallets || walletResponse.data.data || [];
      }
      
      if (!Array.isArray(walletData)) {
        console.warn('Wallet data is not an array, converting to empty array:', walletData);
        walletData = [];
      }
      
      setWallet(walletData);
      
      const totalValue = walletData.reduce((sum, walletItem) => {
        if (!walletItem || walletItem.balance === undefined || walletItem.balance === null) return sum;
        
        const balance = parseFloat(walletItem.balance) || 0;
        
        if (walletItem.currency === 'USDT') {
          return sum + balance;
        } else {
          const coinData = marketData[walletItem.currency];
          if (coinData && coinData.current_price) {
            return sum + (balance * coinData.current_price);
          }
        }
        return sum;
      }, 0);
      
      setPortfolioValue(totalValue);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setWallet([]);
      setLoading(false);
    }
  };

  const portfolioData = [
    { date: 'Jan', value: 8500 },
    { date: 'Feb', value: 9200 },
    { date: 'Mar', value: 8800 },
    { date: 'Apr', value: 9500 },
    { date: 'May', value: 11000 },
    { date: 'Jun', value: 10500 },
    { date: 'Jul', value: 11500 },
    { date: 'Aug', value: 12000 },
    { date: 'Sep', value: 12500 },
    { date: 'Oct', value: portfolioValue }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatCrypto = (amount, decimals = 6) => {
    if (!amount) return '0.00';
    const num = parseFloat(amount) || 0;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: decimals
    });
  };

  const calculateTotalBalance = () => {
    if (!Array.isArray(wallet)) {
      return 0;
    }
    
    return wallet.reduce((sum, walletItem) => {
      if (!walletItem || !walletItem.balance) return sum;
      const balance = parseFloat(walletItem.balance) || 0;
      return sum + balance;
    }, 0);
  };

  const getUSDTBalance = () => {
    if (!Array.isArray(wallet)) return 0;
    const usdtWallet = wallet.find(w => w && w.currency === 'USDT');
    return usdtWallet ? (parseFloat(usdtWallet.balance) || 0) : 0;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  const totalBalance = calculateTotalBalance();
  const usdtBalance = getUSDTBalance();

  // Apply dark theme class based on user authentication
  const dashboardClass = user ? "dashboard" : "dashboard dark-theme";

  return (
    <div className={dashboardClass}>
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard</h1>
          <p>Welcome back! Here's your trading overview</p>
        </div>
        <div className="header-actions">
          <Link to="/trade" className="btn btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Start Trading
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(portfolioValue)}</div>
            <div className="stat-label">Portfolio Value</div>
            <div className="stat-change positive">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12L12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              +12.5%
            </div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 12V8H4V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 15H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(usdtBalance)}</div>
            <div className="stat-label">Available Balance</div>
            <div className="stat-change">Ready to trade</div>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 19V13C9 11.8954 9.89543 11 11 11H13C14.1046 11 15 11.8954 15 13V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M9 19C9 20.1046 9.89543 21 11 21H13C14.1046 21 15 20.1046 15 19V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M9 19V9C9 7.89543 9.89543 7 11 7H13C14.1046 7 15 7.89543 15 9V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{recentOrders.length}</div>
            <div className="stat-label">Active Orders</div>
            <div className="stat-change">This week</div>
          </div>
        </div>

        <div className="stat-card info">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 21V5C20 3.89543 19.1046 3 18 3H6C4.89543 3 4 3.89543 4 5V21" stroke="currentColor" strokeWidth="2"/>
              <rect x="8" y="7" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="2"/>
              <path d="M14 15H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M14 19H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{Array.isArray(wallet) ? wallet.length : 0}</div>
            <div className="stat-label">Assets</div>
            <div className="stat-change">In your wallet</div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Left Column */}
        <div className="dashboard-left">
          {/* Portfolio Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>Portfolio Performance</h3>
              <div className="chart-actions">
                <button 
                  className={`time-btn ${activeTab === '1d' ? 'active' : ''}`}
                  onClick={() => setActiveTab('1d')}
                >
                  1D
                </button>
                <button 
                  className={`time-btn ${activeTab === '1w' ? 'active' : ''}`}
                  onClick={() => setActiveTab('1w')}
                >
                  1W
                </button>
                <button 
                  className={`time-btn ${activeTab === '1m' ? 'active' : ''}`}
                  onClick={() => setActiveTab('1m')}
                >
                  1M
                </button>
                <button 
                  className={`time-btn ${activeTab === '1y' ? 'active' : ''}`}
                  onClick={() => setActiveTab('1y')}
                >
                  1Y
                </button>
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={portfolioData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#94a3b8" 
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#94a3b8" 
                    fontSize={12}
                    tickFormatter={(value) => `$${value/1000}k`} 
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Portfolio Value']}
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#f8fafc'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#3b82f6" 
                    fill="url(#colorValue)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="actions-card">
            <h3>Quick Actions</h3>
            <div className="actions-grid">
              <Link to="/trade" className="action-item">
                <div className="action-icon primary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span>Trade</span>
              </Link>
              <Link to="/wallet" className="action-item">
                <div className="action-icon success">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M20 12V8H4V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 15H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span>Wallet</span>
              </Link>
              <Link to="/markets" className="action-item">
                <div className="action-icon warning">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M18 16L12 10L6 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 12V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20 18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V18Z" 
                          stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <span>Markets</span>
              </Link>
              <Link to="/kyc-verification" className="action-item">
                <div className="action-icon info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C13.6569 3 15.1569 3.67343 16.364 4.7231" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span>KYC</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="dashboard-right">
          {/* Wallet Overview */}
          <div className="wallet-card">
            <div className="card-header">
              <h3>Wallet Overview</h3>
              <Link to="/wallet" className="view-all">View All</Link>
            </div>
            <div className="wallet-list">
              {Array.isArray(wallet) && wallet.length > 0 ? (
                wallet.slice(0, 5).map((walletItem) => (
                  <div key={walletItem.currency} className="wallet-item">
                    <div className="wallet-info">
                      <div className="currency-symbol">{walletItem.currency}</div>
                      <div className="currency-name">
                        {walletItem.currency === 'USDT' ? 'Tether' : 
                         walletItem.currency === 'BTC' ? 'Bitcoin' :
                         walletItem.currency === 'ETH' ? 'Ethereum' : walletItem.currency}
                      </div>
                    </div>
                    <div className="wallet-balance">
                      <div className="balance-amount">
                        {formatCrypto(walletItem.balance, walletItem.currency === 'USDT' ? 2 : 6)}
                      </div>
                      {walletItem.currency !== 'USDT' && marketData[walletItem.currency] && (
                        <div className="balance-value">
                          {formatCurrency(parseFloat(walletItem.balance) * marketData[walletItem.currency].current_price)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-wallet">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <path d="M20 12V8H4V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 15H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p>No wallet data available</p>
                  <Link to="/deposit" className="btn btn-outline">
                    Deposit Funds
                  </Link>
                </div>
              )}
            </div>
            {Array.isArray(wallet) && wallet.length > 5 && (
              <div className="card-footer">
                <Link to="/wallet" className="show-more">
                  +{wallet.length - 5} more assets
                </Link>
              </div>
            )}
          </div>

          {/* Market Overview */}
          <div className="markets-card">
            <div className="card-header">
              <h3>Top Markets</h3>
              <Link to="/markets" className="view-all">View All</Link>
            </div>
            <div className="markets-list">
              {tradingPairs.slice(0, 6).map((pair) => {
                const symbol = pair.split('/')[0];
                const data = marketData[symbol];
                const price = data?.current_price || 0;
                const change = data?.price_change_percentage_24h || 0;
                
                return (
                  <Link 
                    key={pair} 
                    to="/trade" 
                    state={{ selectedPair: pair }}
                    className="market-item"
                  >
                    <div className="market-info">
                      <div className="market-symbol">{symbol}</div>
                      <div className="market-pair">{pair}</div>
                    </div>
                    <div className="market-price">
                      <div className="price">${formatCrypto(price, 6)}</div>
                      <div className={`change ${change >= 0 ? 'positive' : 'negative'}`}>
                        {change >= 0 ? '+' : ''}{change?.toFixed(2)}%
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="activity-card">
            <div className="card-header">
              <h3>Recent Activity</h3>
            </div>
            <div className="activity-list">
              {recentOrders.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <path d="M9 19V13C9 11.8954 9.89543 11 11 11H13C14.1046 11 15 11.8954 15 13V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M9 19C9 20.1046 9.89543 21 11 21H13C14.1046 21 15 20.1046 15 19V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M9 19V9C9 7.89543 9.89543 7 11 7H13C14.1046 7 15 7.89543 15 9V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <p>No recent activity</p>
                  <Link to="/trade" className="btn btn-outline">
                    Start Trading
                  </Link>
                </div>
              ) : (
                recentOrders.map((order, index) => (
                  <div key={index} className="activity-item">
                    <div className={`activity-icon ${order.type}`}>
                      {order.type === 'buy' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M5 12L12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M5 12L12 19L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      )}
                    </div>
                    <div className="activity-details">
                      <div className="activity-title">
                        {order.type.toUpperCase()} {order.pair}
                      </div>
                      <div className="activity-time">
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className={`activity-amount ${order.type}`}>
                      {order.type === 'buy' ? '+' : '-'}{formatCrypto(order.amount)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;